import { ChevronDown, ChevronRight, FolderOpen, LoaderCircle, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useState } from 'react';

import { Switch } from '@/_shared/components/ui/switch';

import { type Repo, RepoCombobox } from './RepoCombobox';
import { ScmProviderIcon } from './ScmProviderIcon';

export interface MappingEntry {
  installationId: number;
  repoFullName: string;
  subfolder: string;
  provider?: 'github' | 'gitlab';
  connectionId?: string;
}

const PROVIDER_LABEL = { github: 'GitHub', gitlab: 'GitLab' } as const;

interface SourceCardProps {
  source: { id: string; name: string; docsEnabled: boolean; prReviewEnabled: boolean };
  mappings: MappingEntry[];
  repos: Repo[];
  isAdmin: boolean;
  onAddRepo: (sourceId: string, repoKey: string, subfolder: string, onSuccess: () => void) => void;
  onRemoveRepo: (sourceId: string, repoFullName: string, provider: 'github' | 'gitlab') => void;
  onDelete: (sourceId: string) => void;
  onToggle: (sourceId: string, docsEnabled: boolean, prReviewEnabled: boolean) => void;
  saving: boolean;
}

export function SourceCard({
  source,
  mappings,
  repos,
  isAdmin,
  onAddRepo,
  onRemoveRepo,
  onDelete,
  onToggle,
  saving,
}: SourceCardProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addRepoKey, setAddRepoKey] = useState('');
  const [addSubfolder, setAddSubfolder] = useState('/');

  const resetAddForm = useCallback(() => {
    setShowAddForm(false);
    setAddRepoKey('');
    setAddSubfolder('/');
  }, []);

  const handleAdd = useCallback(() => {
    if (!addRepoKey) return;
    onAddRepo(source.id, addRepoKey, addSubfolder || '/', () => {
      resetAddForm();
    });
  }, [source.id, addRepoKey, addSubfolder, onAddRepo, resetAddForm]);

  const mappedKeys = new Set(
    mappings.map((m) => `${m.provider ?? 'github'}:${m.connectionId ?? ''}:${m.repoFullName}`),
  );
  const availableRepos = repos.filter(
    (r) => !mappedKeys.has(`${r.provider ?? 'github'}:${r.connectionId ?? ''}:${r.fullName}`),
  );

  // Summary text for collapsed state
  const repoSummary =
    mappings.length === 0
      ? 'No repos'
      : mappings.length === 1
        ? mappings[0]!.repoFullName
        : `${mappings[0]!.repoFullName} +${mappings.length - 1}`;

  return (
    <section aria-label={`Source: ${source.name}`} className="rounded border border-border bg-card">
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${source.name}`}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="size-2 shrink-0 rounded-full bg-green-500" role="img" aria-label="Connected" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{source.name}</span>
        <span className="shrink-0 truncate text-xs text-muted-foreground/60">{repoSummary}</span>
        <button
          type="button"
          onClick={() => onDelete(source.id)}
          disabled={!isAdmin}
          aria-label={`Delete source ${source.name}`}
          className="inline-flex shrink-0 items-center rounded border border-border bg-background p-1 text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-700 disabled:opacity-30"
        >
          <Trash2 size={11} />
        </button>
      </div>

      {/* Expanded: repo list + add form */}
      {expanded && (
        <div className="border-t border-border">
          {/* Repo list */}
          {mappings.length > 0 && (
            <ul className="divide-y divide-border/50" aria-label="Linked repositories">
              {mappings.map((m) => {
                const provider = m.provider ?? 'github';
                return (
                  <li
                    key={`${provider}:${m.connectionId ?? ''}:${m.repoFullName}`}
                    className="flex items-center gap-3 py-2.5 pr-4 pl-12"
                  >
                    <ScmProviderIcon provider={provider} size={12} />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{m.repoFullName}</span>
                    {m.subfolder !== '/' && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        <FolderOpen size={10} />
                        {m.subfolder}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => onRemoveRepo(source.id, m.repoFullName, provider)}
                      disabled={!isAdmin || saving}
                      aria-label={`Remove ${m.repoFullName} (${PROVIDER_LABEL[provider]})`}
                      className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:text-red-700 focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-30"
                    >
                      <X size={12} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {mappings.length === 0 && !showAddForm && (
            <p className="py-4 pr-4 pl-12 text-xs text-muted-foreground/60">No repositories linked. Add one below.</p>
          )}

          {/* Feature toggles */}
          <div className="space-y-2 border-t border-border/50 py-3 pr-4 pl-12">
            <div className="flex items-center justify-between">
              <label htmlFor={`toggle-docs-${source.id}`} className="text-xs text-muted-foreground">
                Docs generation
              </label>
              <Switch
                id={`toggle-docs-${source.id}`}
                size="sm"
                checked={source.docsEnabled}
                onCheckedChange={(checked) => onToggle(source.id, !!checked, source.prReviewEnabled)}
                disabled={!isAdmin}
                aria-label="Docs generation"
              />
            </div>
            <div className="flex items-center justify-between">
              <label htmlFor={`toggle-pr-${source.id}`} className="text-xs text-muted-foreground">
                Auto PR review
              </label>
              <Switch
                id={`toggle-pr-${source.id}`}
                size="sm"
                checked={source.prReviewEnabled}
                onCheckedChange={(checked) => onToggle(source.id, source.docsEnabled, !!checked)}
                disabled={!isAdmin}
                aria-label="Auto PR review"
              />
            </div>
          </div>

          {/* Add repo form */}
          {showAddForm ? (
            <div className="space-y-3 border-t border-border/50 pt-3 pr-4 pb-4 pl-12">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Repository</label>
                <RepoCombobox repos={availableRepos} value={addRepoKey} onChange={setAddRepoKey} />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Subfolder <span className="text-muted-foreground/40">(for monorepos)</span>
                </label>
                <input
                  type="text"
                  value={addSubfolder}
                  onChange={(e) => setAddSubfolder(e.target.value)}
                  placeholder="/"
                  className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-brand"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={!isAdmin || !addRepoKey || saving}
                  className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/20 disabled:opacity-30"
                >
                  {saving && <LoaderCircle size={12} className="animate-spin" />}
                  Add
                </button>
                <button
                  type="button"
                  onClick={resetAddForm}
                  className="inline-flex items-center rounded border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="border-t border-border/50 py-2.5 pr-4 pl-12">
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                disabled={!isAdmin || availableRepos.length === 0}
                className="inline-flex items-center gap-1.5 text-xs text-brand transition-colors hover:text-brand/80 disabled:opacity-30"
              >
                <Plus size={12} />
                Add repo
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
